<template>
  <div class="w-full flex flex-col mx-auto px-4 sm:px-6 lg:px-8">
    <h1 class="text-center text-2xl font-semibold text-gray-900 dark:text-white mb-6">Información Personal</h1>

    <div v-if="paciente" class="w-full flex flex-col mx-auto bg-white p-6 rounded-lg shadow-lg">
      <!-- Foto de Perfil -->
      <div class="text-center mb-6">
        <img v-if="paciente && (paciente.fotoPerfil || true)"
          :src="paciente.fotoPerfil || 'https://static.vecteezy.com/system/resources/previews/020/765/399/original/default-profile-account-unknown-icon-black-silhouette-free-vector.jpg'"
          class="w-24 h-24 rounded-full object-cover mx-auto" alt="Foto de Perfil" />

        <label class="block mt-3 text-sm font-medium text-gray-900 dark:text-white" for="file_input">Subir Foto de
          Perfil</label>
        <div class="file-upload">
          <label for="file_input" class="file-label"> Seleccionar archivo </label>
          <input type="file" @change="onFileChange" id="file_input" class="file-input" />
          <span id="file_name" class="file-name">No se ha seleccionado ningún archivo</span>
        </div>
        <div class="mt-2 text-sm text-gray-500 dark:text-gray-300" id="user_avatar_help">Una foto de perfil es útil para
          identificarte.</div>
      </div>

      <!-- Información editable del paciente -->
      <form class="w-full flex flex-col items-center gap-2">
        <div class="grid gap-6 mb-6 md:grid-cols-2 w-full">
          <div>
            <label for="nombre" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Nombre</label>
            <input v-model="paciente.nombre" type="text" id="nombre"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Juan" required />
          </div>
          <div>
            <label for="apellido" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Apellido</label>
            <input v-model="paciente.apellido" type="text" id="apellido"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Pérez" required />
          </div>

          <div>
            <label for="telefono" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Teléfono</label>
            <input v-model="paciente.telefono" type="tel" id="telefono" pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="123-456-7890" required />
          </div>

          <div>
            <label for="email" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Email</label>
            <input v-model="paciente.email" type="email" id="email"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="juan.perez@mail.com" required />
          </div>

          <div class="form-group mb-3">
            <label>Dirección:</label>
            <input v-model="paciente.direccion" @focus="iniciarGooglePlaces" ref="direccionInput" class="form-control"
              placeholder="Usa Google Places para precisión" />
            <small v-if="direccionInvalida" class="text-danger">Por favor, selecciona una dirección válida de las
              sugerencias.</small>
            <small v-if="direccionValida" class="text-success">Dirección válida seleccionada.</small>
          </div>
        </div>

        
        <!-- Alerta flotante -->
        <div v-if="alertMessage" :class="alertType === 'success'
      ? 'text-green-800 bg-green-50 border-green-300'
      : 'text-red-800 bg-red-50 border-red-300'"
          class="fixed bottom-4 right-4 p-4 border rounded-lg shadow-lg flex items-center space-x-2 z-50 transition-opacity duration-300 ease-in-out"
          role="alert">
          <svg v-if="alertType === 'success'" aria-hidden="true" class="w-5 h-5 text-green-800" fill="currentColor"
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-7h2V5h-2v6zm0 4h2v-2h-2v2z"
              clip-rule="evenodd"></path>
          </svg>
          <svg v-else aria-hidden="true" class="w-5 h-5 text-red-800" fill="currentColor"
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-7h2V5h-2v6zm0 4h2v-2h-2v2z"
              clip-rule="evenodd"></path>
          </svg>
          <span class="font-medium">
            {{ alertType === 'success' ? 'Éxito:' : 'Error:' }}
          </span>
          <span>{{ alertMessage }}</span>
        </div>
         

        <!-- Botón Guardar Cambios con Spinner -->
        <button @click="guardarCambios" type="button" :disabled="isLoading"
          class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex items-center">
          <svg v-if="isLoading" aria-hidden="true" role="status" class="inline w-4 h-4 mr-3 text-white animate-spin"
            viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="#E5E7EB" />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentColor" />
          </svg>
          <span v-if="!isLoading">Guardar Cambios</span>
          <span v-else>Cargando...</span>
        </button>
        <!-- 📄 Subida y vista de documentos -->
<div class="mt-8">
  <h2 class="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Documentos</h2>

  <!-- ✅ Botón con estilo consistente -->
  <label
    class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex items-center cursor-pointer"
  >
    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg">
      <path stroke-linecap="round" stroke-linejoin="round"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0l-4 4m4-4v12" />
    </svg>
    Subir Documento
    <input
      type="file"
      @change="subirDocumento"
      accept=".pdf,.jpg,.jpeg,.png"
      class="hidden"
    />
  </label>


</div>

      </form>
    </div>

    <div v-else>
      <p class="text-center">Cargando información...</p>
    </div>
  </div>
</template>

<script>


import { doc, getDoc, updateDoc, addDoc, collection } from "firebase/firestore"; // Solo funciones de Firestore aquí
import { ref, uploadBytes, getDownloadURL, getStorage, listAll  } from "firebase/storage"; // Storage separado
import { db } from '@/firebase';
import { getAuth } from 'firebase/auth';

export default {
  data() {
    return {
      isLoading: false, // Estado para el spinner
      alertMessage: "", // Mensaje de alerta
      alertType: "", // Tipo de alerta ('success' o 'error')
      direccionInvalida: false, // Validación para la dirección
      direccionValida: false, // Estado para mostrar el mensaje de confirmación de dirección válida
      autocomplete: null, // Instancia de Google Places Autocomplete
      documentos: [],
      paciente: {
        nombre: "",
        apellido: "",
        direccion: "",
        telefono: "",
        email: "",
        
        
        fotoPerfil: "",
        coordenadas: null,
        

      }
    };
  },
  methods: {
    async cargarPaciente() {
      try {
        // Obtener el usuario autenticado
        const auth = getAuth();
        const user = auth.currentUser;

        // Verificar si el usuario está autenticado
        if (!user) {
          throw new Error("No estás autenticado. Inicia sesión para continuar.");
        }

        // Usar el UID del usuario autenticado como el pacienteId
        const pacienteId = user.uid;

        if (!pacienteId) {
          throw new Error("El ID del paciente no está disponible.");
        }

        // Acceder al documento del paciente en Firestore
        const docRef = doc(db, "pacientes", pacienteId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          this.paciente = docSnap.data();  // Guardar los datos del paciente
        } else {
          console.error("No se encontró el perfil del paciente.");
          this.mostrarAlerta("error", "No se encontró el perfil del paciente.");
        }
      } catch (error) {
        console.error("Error al cargar los datos del paciente:", error);
        this.mostrarAlerta("error", "Ocurrió un error al cargar los datos del paciente.");
      }
    },
    async onFileChange(e) {
  const file = e.target.files[0];
  if (!file || !this.paciente || !this.paciente.uid) return;

  try {
    const storage = getStorage();
    const fileRef = ref(storage, `pacientes/${this.paciente.uid}/fotoPerfil.jpg`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);

    this.paciente.fotoPerfil = url;

    // 🔁 ACTUALIZA Firestore inmediatamente
    const pacienteDocRef = doc(db, "pacientes", this.paciente.uid);
    await updateDoc(pacienteDocRef, {
      fotoPerfil: url
    });

    this.mostrarAlerta("success", "Foto subida y guardada correctamente.");
  } catch (error) {
    console.error("Error al subir la foto:", error);
    this.mostrarAlerta("error", "Error al subir la foto de perfil.");
  }

  const fileInput = e.target;
  const fileName = fileInput.files.length > 0 ? fileInput.files[0].name : "No se ha seleccionado ningún archivo";
  document.getElementById("file_name").innerText = fileName;
},

    

    async subirDocumento(event) {
    const file = event.target.files[0];
    if (!file || !this.paciente || !this.paciente.uid) return;

    try {
      const storage = getStorage();
      const storageRef = ref(storage, `documentos/${this.paciente.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await addDoc(collection(db, `pacientes/${this.paciente.uid}/documentos`), {
        nombre: file.name,
        url,
        timestamp: new Date()
      });

      this.mostrarAlerta('success', 'Documento subido correctamente.');
      await this.cargarDocumentos();
    } catch (error) {
      console.error("Error subiendo documento:", error);
      this.mostrarAlerta('error', 'Error al subir documento.');
    }
  },

  async cargarDocumentos() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const storage = getStorage();
    const path = `pacientes/${user.uid}/documentos`;
    const listRef = ref(storage, path);
    const result = await listAll(listRef);

    this.documentos = await Promise.all(result.items.map(async (itemRef) => {
      const url = await getDownloadURL(itemRef);
      return {
        nombre: itemRef.name,
        url
      };
    }));
  } catch (e) {
    console.error("Error al cargar documentos:", e);
  }
},


    async guardarCambios() {
      if (this.direccionInvalida) {
        this.mostrarAlerta('error', 'Por favor, selecciona una dirección válida de las sugerencias.');
        return;
      }

      this.isLoading = true; // Activa el spinner
      try {
        const pacienteId = this.$route.params.pacienteId;
        if (!pacienteId) {
          throw new Error("El ID del paciente no está disponible.");
        }

        const docRef = doc(db, "pacientes", pacienteId);

        // Prepara los datos que se van a guardar
        const datosActualizados = {
          nombre: this.paciente.nombre,
          apellido: this.paciente.apellido,
          direccion: this.paciente.direccion,
          telefono: this.paciente.telefono,
          email: this.paciente.email,
          
          
          fotoPerfil: this.paciente.fotoPerfil || "",
          coordenadas: this.paciente.coordenadas || null
        };

        await updateDoc(docRef, datosActualizados);

        this.mostrarAlerta('success', 'Información actualizada exitosamente.');
      } catch (error) {
        console.error("Error al actualizar la información:", error);
        this.mostrarAlerta('error', 'Hubo un error al actualizar la información. Por favor, intenta nuevamente.');
      } finally {
        this.isLoading = false; // Desactiva el spinner
      }
    },
    mostrarAlerta(tipo, mensaje) {
      this.alertType = tipo;
      this.alertMessage = mensaje;

      // Ocultar la alerta automáticamente después de 5 segundos
      setTimeout(() => {
        this.alertMessage = "";
        this.alertType = "";
      }, 5000);
    },
  },
  async mounted() {
    try {
      
      await this.cargarPaciente();    // Carga los datos del paciente
      await this.cargarDocumentos(); // Carga los documentos (nuevo)
    } catch (error) {
      console.error("Error al montar el componente:", error);
      this.mostrarAlerta("error", "Ocurrió un error al cargar los datos.");
    }
  }
};
</script>

<style scoped>
.file-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.file-label {
  background-color: #007BFF;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
}

.file-input {
  display: none;
}

.file-name {
  margin-top: 10px;
  text-align: center;
}

.container {
  max-width: 100%;
}

.max-w-3xl {
  max-width: 750px;
  /* Limita el formulario a un tamaño adecuado en pantallas grandes */
}

input,
textarea {
  font-size: 1rem;
}

button {
  padding: 10px 15px;
  font-size: 16px;
}

@media (max-width: 1024px) {
  .container {
    padding: 0 1rem;

  }
}
</style>